import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button, Spinner, TimeFromNow } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';
import { ControlledCodeInput, validation } from '@myhealth-raccoon/forms';

import { authUserService } from 'src/services/authUser';

const DELAY = 60000;

export type VerificationCodeFormValues = {
    code: string;
}

type Props = {
    onCancel?(): unknown;
    onSubmit(values: VerificationCodeFormValues): Promise<unknown>;
    email: string;
}

export function VerificationCodeForm({ onSubmit, onCancel, email }: Props) {

    const [resendTime, setResendTime] = useState<number>();
    const [resendIsDisabled, setResendIsDisabled] = useState<boolean>();
    const [didSubmit, setDidSubmit] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { handleSubmit, control, formState: { isSubmitting, errors }, watch, setError, reset } = useForm<VerificationCodeFormValues>();

    const { invoke: generateCode, isLoading: generatingCode } = useAsyncData({
        service: authUserService,
        method: authUserService.EmailGenerateAndSend,
        invokeOnMount: false,
    }, email);

    const doSubmit = useCallback(async (values: VerificationCodeFormValues) => {
        setDidSubmit(true);
        try {
            await onSubmit(values);
        } catch (e) {
            if (e instanceof Error) {
                setErrorMessage(e.message || e.name);
                setError("code", { message: e.message || e.name })
            }
            reset();
            setDidSubmit(false);
        }
    }, [onSubmit, reset, setError]);

    const resendCode = useCallback(async () => {
        try {
            await generateCode();
            toast("We've sent a new code!");
            setResendIsDisabled(true);
        } catch {
            /* */
        }
    }, [generateCode]);

    useEffect(() => {
        if (resendIsDisabled) {
            const timeout = setTimeout(() => {
                setResendIsDisabled(false);
                setResendTime(undefined);
            }, DELAY);
            setResendTime(Date.now() + DELAY);

            return () => clearTimeout(timeout);
        }
    }, [resendIsDisabled]);

    useEffect(() => {
        if (!didSubmit) {
            handleSubmit(doSubmit)();
        }
        const { unsubscribe } = watch(() => {
            if (!didSubmit) {
                handleSubmit(doSubmit)();
            }
        });
        return unsubscribe;
    }, [handleSubmit, doSubmit, watch, didSubmit]);

    return <>
        <h1 className='text-lg text-gray-900 font-semibold'>Enter verification code</h1>

        <p className='my-2.5 text-sm break-words'>
            For your security, we need to verify your email address. We've sent a verification code to
            <span className='font-semibold'> {email}</span>.
            <p className='mt-5 text-sm'>
                The code will expire after 5 minutes.
            </p>
        </p>

        <form onSubmit={handleSubmit(doSubmit)} className='flex flex-col gap-y-6 mt-4 '>
            <ControlledCodeInput
                control={control}
                name='code'
                fields={6}
                placeholder='000000'
                className='w-fit text-4xl'
                rules={{
                    validate: validation.composeValidators(
                        validation.required(" "),
                        validation.minLength(6, " "))
                }}
                fieldError={!watch("code") && errorMessage ? errors.code : undefined}
            />
            {errorMessage &&
                <div className='text-red-500'>
                    {errorMessage}
                </div>
            }
            <div className='text-sm'>
                Didn't receive the code?{' '}
                <button type='button' disabled={generatingCode || resendIsDisabled} className='font-semibold text-primary disabled:text-gray-300' onClick={resendCode}>
                    Click to resend
                </button>
                {' '}
                {generatingCode && <Spinner size='xs' color='current' className='inline-block align-middle' />}
                {'  '}
                {resendIsDisabled && resendTime &&
                    <TimeFromNow explicit time={resendTime} />
                }
            </div>
            <div className='flex flex-row gap-3'>
                {onCancel && <Button
                    onClick={onCancel}
                    className='flex-1 text-center' color='gray'>Cancel</Button>}
                <Button className='flex-1 text-center' disabled={isSubmitting} type='submit'>Confirm</Button>
            </div>
        </form>
    </>
}